import CustomerPartNumberForm from "@auer-steel/CustomComponents/MyPartNumbers/CustomerPartNumberForm";
import { MyPartNumbersPageContext } from "@auer-steel/Pages/MyPartNumbersPage";
import { CustomerPartNumber } from "@auer-steel/Services/AuerSteelApiModels";
import { AuerState } from "@auer-steel/Store/AuerState";
import { CustomerPartNumbersDataViewContext } from "@auer-steel/Store/Data/CustomerPartNumbers/CustomerPartNumbersSelector";
import deleteCustomerPartNumber from "@auer-steel/Store/Data/CustomerPartNumbers/Handlers/DeleteCustomerPartNumber";
import updateCustomerPartNumber from "@auer-steel/Store/Data/CustomerPartNumbers/Handlers/UpdateCustomerPartNumber";
import resetCustomerPartNumberData from "@auer-steel/Store/Pages/MyPartNumbers/Handlers/ResetCustomerPartNumbers";
import updateSearchFields from "@auer-steel/Store/Pages/MyPartNumbers/Handlers/UpdateSearchFields";
import { createFromProduct } from "@insite/client-framework/Common/ProductInfo";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { ProductContextModel } from "@insite/client-framework/Components/ProductContext";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import ProductImage from "@insite/content-library/Components/ProductImage";
import Clickable, { ClickableProps } from "@insite/mobius/Clickable";
import DataTable, { DataTableProps, SortOrderOptions } from "@insite/mobius/DataTable";
import DataTableBody from "@insite/mobius/DataTable/DataTableBody";
import DataTableCell, { DataTableCellProps } from "@insite/mobius/DataTable/DataTableCell";
import DataTableHead from "@insite/mobius/DataTable/DataTableHead";
import DataTableHeader, { DataTableHeaderPresentationProps } from "@insite/mobius/DataTable/DataTableHeader";
import DataTableRow from "@insite/mobius/DataTable/DataTableRow";
import Hidden from "@insite/mobius/Hidden";
import LazyImage from "@insite/mobius/LazyImage";
import Link from "@insite/mobius/Link";
import LoadingSpinner, { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner";
import Modal, { ModalPresentationProps } from "@insite/mobius/Modal";
import OverflowMenu from "@insite/mobius/OverflowMenu";
import { HasToasterContext, withToaster } from "@insite/mobius/Toast/ToasterContext";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import * as React from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

interface OwnProps extends WidgetProps {}

const mapStateToProps = (state: AuerState) => {
    return {
        parameter: state.myPartNumbers.getCustomerPartNumbersParameter,
        isReordering: state.myPartNumbers.isReordering,
        language: state.context.session.language,
        isSubmitting: state.myPartNumbers.isSubmitting,
    };
};

const mapDispatchToProps = {
    updateSearchFields,
    updateCustomerPartNumber,
    deleteCustomerPartNumber,
    resetCustomerPartNumberData,
};

type Props = ReturnType<typeof mapStateToProps> &
    OwnProps &
    ResolveThunks<typeof mapDispatchToProps> &
    HasToasterContext;

export interface MyPartNumbersTableStyles {
    container?: InjectableCss;
    headerClickable?: ClickableProps;
    headerText?: TypographyProps;
    centeringWrapper?: InjectableCss;
    spinner?: LoadingSpinnerProps;
    noResultsContainer?: InjectableCss;
    noResultsText?: TypographyProps;
    dataTable?: DataTableProps;
    productImageHeader?: DataTableHeaderPresentationProps;
    productNumberHeader?: DataTableHeaderPresentationProps;
    productTitleHeader?: DataTableHeaderPresentationProps;
    customerPartNumbersHeader?: DataTableHeaderPresentationProps;
    myPartNumberActionsHeader?: DataTableHeaderPresentationProps;
    productImageCells?: DataTableCellProps;
    productNumberCells?: DataTableCellProps;
    productTitleCells?: DataTableCellProps;
    customerPartNumberCells?: DataTableCellProps;
    actionsCells?: DataTableCellProps;
    modalProps?: ModalPresentationProps;
    actionButtonsContainer?: InjectableCss;
}

export const myPartNumbersTableStyles: MyPartNumbersTableStyles = {
    container: {
        css: css`
            overflow: auto;
        `,
    },
    centeringWrapper: {
        css: css`
            height: 300px;
            display: flex;
            align-items: center;
        `,
    },
    spinner: {
        css: css`
            margin: auto;
        `,
    },
    noResultsContainer: {
        css: css`
            text-align: center;
            padding: 20px;
        `,
    },
    noResultsText: {
        variant: "h4",
    },
    headerClickable: {
        css: css`
            width: 100%;
            justify-content: space-between;
        `,
    },
    productNumberCells: {
        tight: true,
    },
    actionsCells: {
        tight: true,
    },
    actionButtonsContainer: {
        css: css`
            display: flex;
            flex-direction: column;
        `,
    },
    productImageCells: {
        css: css`
            text-align: center;
        `,
    },
};

const styles = myPartNumbersTableStyles;

class MyPartNumbersTable extends React.Component<Props> {
    static contextType = CustomerPartNumbersDataViewContext;
    context!: React.ContextType<typeof CustomerPartNumbersDataViewContext>;

    headerClick(sortField: string) {
        const sort = this.props.parameter.sort === sortField ? `${sortField} DESC` : sortField;
        this.props.updateSearchFields({ sort });
    }

    sorted = (sortField: string) => {
        let sorted: boolean | string = false;
        if (this.props.parameter.sort === sortField) {
            sorted = "ascending";
        } else if (this.props.parameter.sort === `${sortField} DESC`) {
            sorted = "descending";
        }
        return sorted as SortOrderOptions;
    };

    render() {
        const myPartNumbersDataView = this.context;
        if (myPartNumbersDataView.isLoading) {
            return (
                <StyledWrapper {...styles.centeringWrapper}>
                    <LoadingSpinner {...styles.spinner} />
                </StyledWrapper>
            );
        }

        if (!myPartNumbersDataView.value) {
            return null;
        }

        if (myPartNumbersDataView.value.length === 0) {
            return (
                <StyledWrapper {...styles.noResultsContainer}>
                    <Typography as="p" {...styles.noResultsText} data-test-selector="myPartNumbersTable_noOrdersFound">
                        {translate("No Parts Found")}
                    </Typography>
                </StyledWrapper>
            );
        }

        const rows = myPartNumbersDataView.value.map(customerPartNumber => {
            const productContext: ProductContextModel = {
                product: customerPartNumber.product,
                productInfo: createFromProduct(customerPartNumber.product),
            };
            return {
                id: customerPartNumber.id,
                customerPartNumber,
                productNumber: customerPartNumber.product.productNumber,
                productTitle: customerPartNumber.product.productTitle,
                customerPartNumbers: customerPartNumber.customerPartNumbers,
                productContext,
            };
        });

        return (
            <StyledWrapper {...styles.container} data-test-selector="myPartNumbersTable">
                <DataTable {...styles.dataTable}>
                    <DataTableHead>
                        <DataTableHeader {...styles.productImageHeader} title={translate("")}></DataTableHeader>
                        <DataTableHeader
                            {...styles.productNumberHeader}
                            title={translate("Product Number")}
                            sorted={this.sorted("productNumber")}
                            onSortClick={() => this.headerClick("productNumber")}
                        >
                            {translate("Product Number")}
                        </DataTableHeader>
                        <DataTableHeader
                            {...styles.productTitleHeader}
                            sorted={this.sorted("productTitle")}
                            onSortClick={() => this.headerClick("productTitle")}
                        >
                            {translate("Description")}
                        </DataTableHeader>
                        <DataTableHeader
                            {...styles.customerPartNumbersHeader}
                            sorted={this.sorted("customerPartNumbers")}
                            onSortClick={() => this.headerClick("customerPartNumbers")}
                        >
                            {translate("My Part Numbers")}
                        </DataTableHeader>
                        <DataTableHeader {...styles.myPartNumberActionsHeader}>{translate("")}</DataTableHeader>
                    </DataTableHead>
                    <DataTableBody data-test-selector="myPartNumbersTable_tableBody">
                        {rows.map(
                            ({
                                id,
                                customerPartNumber,
                                productNumber,
                                productTitle,
                                customerPartNumbers,
                                productContext,
                            }) => (
                                <DataTableRow key={id}>
                                    <DataTableCell
                                        {...styles.productImageCells}
                                        data-test-selector="myPartNumbersTable_tableCell_productImage"
                                    >
                                        <LazyImage
                                            src={productContext.product?.smallImagePath}
                                            altText={productContext.product?.imageAltText}
                                        />
                                    </DataTableCell>
                                    <DataTableCell
                                        {...styles.productNumberCells}
                                        data-test-selector="myPartNumbersTable_tableCell_productNumber"
                                    >
                                        {productNumber}
                                    </DataTableCell>
                                    <DataTableCell
                                        {...styles.productTitleCells}
                                        data-test-selector="myPartNumbersTable_tableCell_productTitle"
                                    >
                                        {productTitle}
                                    </DataTableCell>
                                    <DataTableCell
                                        {...styles.customerPartNumberCells}
                                        data-test-selector="myPartNumbersTable_tableCell_customerPartNumbersDisplay"
                                    >
                                        {customerPartNumbers.map((partNumber, index) => (
                                            // eslint-disable-next-line react/no-array-index-key
                                            <StyledWrapper key={`${partNumber}${index}`}>{partNumber}</StyledWrapper>
                                        ))}
                                    </DataTableCell>
                                    <DataTableCell
                                        {...styles.actionsCells}
                                        data-test-selector="myPartNumbersTable_tableCell_actions"
                                    >
                                        <CustomerPartNumberActions
                                            customerPartNumber={customerPartNumber}
                                            updateCustomerPartNumber={this.props.updateCustomerPartNumber}
                                            deleteCustomerPartNumber={this.props.deleteCustomerPartNumber}
                                            resetCustomerPartNumberData={this.props.resetCustomerPartNumberData}
                                            isSubmitting={this.props.isSubmitting}
                                        />
                                    </DataTableCell>
                                </DataTableRow>
                            ),
                        )}
                    </DataTableBody>
                </DataTable>
            </StyledWrapper>
        );
    }
}

interface CustomerPartNumberActionsProps {
    customerPartNumber: CustomerPartNumber;
    updateCustomerPartNumber: Props["updateCustomerPartNumber"];
    deleteCustomerPartNumber: Props["deleteCustomerPartNumber"];
    isSubmitting: boolean;
    resetCustomerPartNumberData: Props["resetCustomerPartNumberData"];
}

const CustomerPartNumberActions: React.FunctionComponent<CustomerPartNumberActionsProps> = (
    props: CustomerPartNumberActionsProps,
) => {
    const [modalIsOpen, setModalIsOpen] = React.useState(false);

    const { customerPartNumber, isSubmitting } = props;

    const modalCloseHandler = () => {
        props.resetCustomerPartNumberData();
        setModalIsOpen(false);
    };

    const editClickHandler = () => {
        setModalIsOpen(true);
    };

    const formCancelHandler = (e: any) => {
        e.preventDefault();
        modalCloseHandler();
    };

    const formSubmitHandler = (e: any, customerPartNumber: CustomerPartNumber) => {
        e.preventDefault();
        modalCloseHandler();
    };

    const deleteClickHandler = (e: any) => {
        props.deleteCustomerPartNumber(customerPartNumber);
    };

    return (
        <>
            <Hidden above="sm">
                <OverflowMenu position="end">
                    <Clickable onClick={editClickHandler}>{translate("Edit")}</Clickable>
                    <Clickable onClick={deleteClickHandler}>{translate("Delete")}</Clickable>
                </OverflowMenu>
            </Hidden>
            <Hidden below="md">
                <StyledWrapper {...styles.actionButtonsContainer}>
                    <Link onClick={editClickHandler}>{translate("Edit")}</Link>
                    <Link color="secondary" onClick={deleteClickHandler}>
                        {translate("Delete")}
                    </Link>
                </StyledWrapper>
            </Hidden>
            <Modal
                {...styles.modalProps}
                isOpen={modalIsOpen}
                isCloseable={!isSubmitting}
                handleClose={modalCloseHandler}
                headline={translate("Edit Part Numbers")}
            >
                <CustomerPartNumberForm
                    formType="update"
                    customerPartNumber={customerPartNumber}
                    onSubmit={formSubmitHandler}
                    onCancel={formCancelHandler}
                    isSubmitting={isSubmitting}
                />
            </Modal>
        </>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withToaster(MyPartNumbersTable)),
    definition: {
        group: "My Part Numbers" as any,
        displayName: "Search Results Table",
        allowedContexts: [MyPartNumbersPageContext],
    },
};

export default widgetModule;
